import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAppConfig } from '../state/config';
import { useCoreOnly } from '../state/coreOnly';
import { useGaps } from '../state/gaps';
import { useGeoFilter } from '../state/geoFilter';
import { useStreams } from '../state/streams';
import { streamIcon, streamLabel } from '../stream-meta';
import type { StreamSummary } from '../types';
import { GEO_ALL } from '../types';

/**
 * Index of streams (spec §3 step 2 + §9.1).
 *
 * Reads the snapshot-driven `/api/streams` endpoint so the card counts respect
 * the latest sync — and split into core / total per the "Core forms only"
 * toggle. The per-stream gap badge keeps using the user-zone gap store.
 */
export function StreamsIndex() {
  const [showAll, setShowAll] = useState(false);
  const { streams, loading, error } = useStreams(showAll);
  const { geographies } = useAppConfig();
  const { coreOnly } = useCoreOnly();
  const { geo } = useGeoFilter(undefined);

  const geoFilters = useMemo(
    () => (geo === GEO_ALL ? {} : { geography: geo }),
    [geo],
  );
  const { gaps } = useGaps(geoFilters);

  const gapsByStream = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of gaps) {
      if (!g.stream) continue;
      m.set(g.stream, (m.get(g.stream) ?? 0) + 1);
    }
    return m;
  }, [gaps]);

  const totals = useMemo(() => {
    let totalForms = 0;
    let totalCore = 0;
    for (const s of streams) {
      totalForms += s.form_count_total;
      totalCore += s.form_count_core;
    }
    return { totalForms, totalCore, streamsCount: streams.length };
  }, [streams]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Select a Stream</h1>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-brand-danone focus:ring-brand-danone"
          />
          Show all streams
          <span
            className="text-slate-400"
            title="Includes DBS apps outside the AME LPO review scope (BSRP, CAT, BRM, DSP, …)"
          >
            (incl. out-of-scope)
          </span>
        </label>
      </header>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-md px-4 py-3">
          Couldn&apos;t load the streams: {error}
        </div>
      )}

      {loading && streams.length === 0 && (
        <div className="text-slate-500">Loading streams…</div>
      )}

      {streams.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {streams.map((s) => (
              <StreamCard
                key={s.id}
                stream={s}
                coreOnly={coreOnly}
                gapCount={gapsByStream.get(s.id) ?? 0}
              />
            ))}
          </div>

          <footer className="mt-8 text-xs text-slate-500">
            {totals.streamsCount} stream{totals.streamsCount === 1 ? '' : 's'} ·{' '}
            {coreOnly ? totals.totalCore : totals.totalForms} forms
            {!coreOnly && totals.totalCore !== totals.totalForms && (
              <>
                {' '}({totals.totalCore} core,{' '}
                {totals.totalForms - totals.totalCore} non-core)
              </>
            )}
            {' '} across {geographies.length} geographies
          </footer>
        </>
      )}
    </div>
  );
}

function StreamCard({
  stream,
  coreOnly,
  gapCount,
}: {
  stream: StreamSummary;
  coreOnly: boolean;
  gapCount: number;
}) {
  const formCount = coreOnly ? stream.form_count_core : stream.form_count_total;
  const nonCore = stream.form_count_total - stream.form_count_core;

  return (
    <Link
      to={`/streams/${encodeURIComponent(stream.id)}`}
      className={`bg-white border-2 rounded-xl p-6 text-center hover:border-brand-danone hover:shadow-md transition flex flex-col items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-200 ${
        stream.in_scope
          ? 'border-slate-200'
          : 'border-slate-200 border-dashed bg-slate-50'
      }`}
      title={stream.in_scope ? undefined : 'Out of LPO review scope'}
    >
      <div className="text-4xl mb-3 leading-none" aria-hidden>
        {streamIcon(stream.id)}
      </div>
      <div className="text-xl font-bold text-slate-900 tracking-tight">
        {stream.id}
        {!stream.in_scope && (
          <span className="ml-1.5 text-[9px] uppercase tracking-wide text-slate-400 align-middle">
            out of scope
          </span>
        )}
      </div>
      <div className="text-sm text-slate-500 mt-1.5 leading-snug">
        {streamLabel(stream.id) || stream.name}
      </div>
      <div className="text-base text-brand-danone mt-3 font-semibold">
        {formCount} forms
      </div>
      {!coreOnly && nonCore > 0 && (
        <div className="text-xs text-slate-400 mt-0.5">
          {stream.form_count_core} core · {nonCore} non-core
        </div>
      )}
      {gapCount > 0 && (
        <div className="mt-2 text-xs font-bold text-red-600">
          {gapCount} gap{gapCount === 1 ? '' : 's'}
        </div>
      )}
    </Link>
  );
}
